import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ClipboardList,
  ListChecks,
  Lock,
  Plus,
  ShoppingCart,
  Trash2,
  Unlock,
} from "lucide-react";

import {
  useProject,
  useReleaseProjectReservations,
  useRemoveRequirement,
  useReserveForProject,
} from "@/api/queries";
import type { CoverageLine } from "@/api/types";
import { useCanWrite } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDate,
  formatProjectStatus,
  formatQuantity,
  formatRequirementStatus,
  formatUnit,
  pluralize,
} from "@/lib/format";
import { projectTone, requirementTone } from "@/lib/stock";
import { ConfirmFromListDialog } from "@/features/requirements/ConfirmFromListDialog";
import { NetRequirementsPanel } from "@/features/requirements/NetRequirementsPanel";
import { SuggestionsPanel } from "@/features/requirements/SuggestionsPanel";
import { AddRequirementDialog } from "./AddRequirementDialog";
import { ShortfallDialog } from "./ShortfallDialog";

/**
 * The preparation table (Frontend Spec §18, PHASE 9).
 *
 * Eight columns, and the reason the screen exists is the gap between two of
 * them: *Physique* is what the company owns, *Disponible réel* is what this job
 * can actually have. They differ by everything another job already reserved,
 * and a system that shows only the first one sends people to a shelf that is
 * already spoken for.
 *
 * Every figure comes from the server. The client does not add, subtract or
 * infer a single one of them.
 */
export function ProjectDetailPage() {
  const { projectId = "" } = useParams();
  const canWrite = useCanWrite();
  const toast = useToast();

  const { data, isLoading, error, refetch } = useProject(projectId);
  const reserve = useReserveForProject();
  const release = useReleaseProjectReservations();
  const removeLine = useRemoveRequirement();

  const [adding, setAdding] = useState(false);
  const [showingShortfall, setShowingShortfall] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const anythingHeld = useMemo(
    () => data?.lines.some((line) => Number(line.reserved_for_project) > 0) ?? false,
    [data],
  );
  const anythingReservable = useMemo(
    () => data?.lines.some((line) => Number(line.reservable) > 0) ?? false,
    [data],
  );

  if (isLoading) return <LoadingState label="Chargement du projet…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { project, lines } = data;

  async function reserveAll() {
    try {
      const result = await reserve.mutateAsync({ projectId });
      toast.success(
        result.transactions === 0
          ? "Rien à réserver"
          : `${pluralize(result.transactions, "réservation")} posée(s)`,
        result.transactions === 0
          ? "Tout ce qui était libre est déjà réservé pour ce projet."
          : undefined,
      );
    } catch (caught) {
      toast.error("Réservation refusée", String(caught));
    }
  }

  async function releaseAll() {
    try {
      const result = await release.mutateAsync({ projectId });
      toast.success(`${pluralize(result.transactions, "libération")} posée(s)`);
    } catch (caught) {
      toast.error("Libération refusée", String(caught));
    }
  }

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: "Projets / Chantiers", to: "/projects" }, { label: project.code }]}
        title={project.name}
        subtitle={[project.code, project.reference, project.client_name]
          .filter(Boolean)
          .join(" · ")}
        actions={
          canWrite && (
            <>
              <Button variant="secondary" onClick={() => setConfirming(true)}>
                <ListChecks className="size-4" aria-hidden />
                Confirmer une liste
              </Button>
              <Button variant="secondary" onClick={() => setShowingShortfall(true)}>
                <ShoppingCart className="size-4" aria-hidden />
                Liste d'achat
              </Button>
              {anythingHeld && (
                <Button variant="secondary" onClick={() => void releaseAll()} disabled={release.isPending}>
                  <Unlock className="size-4" aria-hidden />
                  Tout libérer
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => void reserveAll()}
                disabled={!anythingReservable || reserve.isPending}
                title={
                  anythingReservable
                    ? undefined
                    : "Rien de libre à réserver pour ce projet en ce moment."
                }
              >
                <Lock className="size-4" aria-hidden />
                Réserver les disponibles
              </Button>
            </>
          )
        }
      />

      <div className="flex min-w-0 flex-col gap-3">
        <div className="grid min-w-0 grid-cols-2 gap-3 @3xl:grid-cols-4">
          <Summary label="Statut">
            <StatusBadge tone={projectTone(project.status)}>
              {formatProjectStatus(project.status)}
            </StatusBadge>
          </Summary>
          <Summary label="Lignes">{data.lines_total}</Summary>
          <Summary label="Couvertes">
            <span className="text-status-available">{data.lines_ok}</span>
          </Summary>
          <Summary label="Manquantes">
            <span className={data.lines_short > 0 ? "text-status-out" : undefined}>
              {data.lines_short}
            </span>
          </Summary>
        </div>

        <Card>
          <CardHeader
            title="Préparation"
            action={
              canWrite && (
                <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
                  <Plus className="size-4" aria-hidden />
                  Ajouter un article
                </Button>
              )
            }
          />
          <div className="min-w-0">
            {lines.length === 0 ? (
              <EmptyState
                icon={<ClipboardList className="size-6" aria-hidden />}
                title="Aucun besoin listé"
                description="Ajoutez les articles que le chantier demande. HD-Vault dira ce qui est réellement disponible et ce qui manque."
              />
            ) : (
              <>
                <div className="hidden md:block">
                  <TableShell>
                    <thead>
                      <tr>
                        <Th className="max-w-0">Article</Th>
                        <Th align="right" className="whitespace-nowrap">
                          Besoin
                        </Th>
                        {/* Measured with scripts/table-min-widths.mjs, container px:
                            7 cols ~ 885 -> @4xl · 8 cols ~ 962 -> @5xl
                            9 cols ~1039 -> @6xl
                            One step lower each and the table overflowed: the
                            operator would scroll sideways to reach Manquant,
                            which is the column the screen exists for. */}
                        <Th align="right" className="hidden whitespace-nowrap @5xl:table-cell">
                          Physique
                        </Th>
                        <Th align="right" className="hidden whitespace-nowrap @6xl:table-cell">
                          Réservé ailleurs
                        </Th>
                        <Th align="right" className="hidden whitespace-nowrap @4xl:table-cell">
                          Disponible réel
                        </Th>
                        <Th align="right" className="whitespace-nowrap">
                          Réservé projet
                        </Th>
                        <Th align="right" className="whitespace-nowrap">
                          Manquant
                        </Th>
                        <Th>Statut</Th>
                        <Th align="right">Action</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <LineRow
                          key={line.requirement_id}
                          line={line}
                          projectId={projectId}
                          canWrite={canWrite}
                          onRemove={async () => {
                            try {
                              await removeLine.mutateAsync({
                                projectId,
                                requirementId: line.requirement_id,
                              });
                            } catch (caught) {
                              toast.error("Ligne non retirée", String(caught));
                            }
                          }}
                        />
                      ))}
                    </tbody>
                  </TableShell>
                </div>

                <ul className="divide-y divide-border/60 md:hidden">
                  {lines.map((line) => (
                    <li key={line.requirement_id}>
                      <LineCard line={line} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Card>

        {(project.site_address || project.notes || project.starts_on) && (
          <Card>
            <CardHeader title="Chantier" />
            <CardBody className="flex flex-col gap-2 text-[13px]">
              {(project.starts_on || project.ends_on) && (
                <p className="text-ink-muted">
                  Du {formatDate(project.starts_on)} au {formatDate(project.ends_on)}
                </p>
              )}
              {project.site_address && <p className="text-ink-muted">{project.site_address}</p>}
              {project.notes && <p className="text-ink-muted">{project.notes}</p>}
            </CardBody>
          </Card>
        )}

        {/* Deux tableaux, deux questions.
            « Préparation » dit ce que ce chantier peut **réserver maintenant**.
            « Besoin net » dit ce qui manque **après** transferts possibles et
            commandes en route — c'est ce qu'on va chercher, pas ce qu'on
            verrouille. Les fusionner ferait une table de onze colonnes qui
            répond mal aux deux. */}
        <Card>
          <CardHeader
            title="Besoin net"
            subtitle="Ce qui manque vraiment, après ce qu'on peut faire venir et ce qui est déjà commandé."
          />
          <CardBody>
            <NetRequirementsPanel projectId={projectId} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Suggestions à vérifier" />
          <CardBody>
            <SuggestionsPanel projectId={projectId} />
          </CardBody>
        </Card>
      </div>

      <ConfirmFromListDialog
        open={confirming}
        onOpenChange={setConfirming}
        projectId={projectId}
      />
      <AddRequirementDialog open={adding} onOpenChange={setAdding} projectId={projectId} />
      <ShortfallDialog
        open={showingShortfall}
        onOpenChange={setShowingShortfall}
        projectId={projectId}
        projectCode={project.code}
      />
    </Page>
  );
}

function Summary({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
      <p className="tnum mt-1 text-[18px] font-semibold text-ink">{children}</p>
    </div>
  );
}

function Quantity({
  value,
  unit,
  tone,
}: {
  value: string;
  unit: string;
  tone?: "muted" | "warn" | "bad";
}) {
  const colour =
    tone === "bad" ? "text-status-out" : tone === "warn" ? "text-status-reserved" : "text-ink-muted";
  return (
    <span className={`tnum whitespace-nowrap ${value === "0" ? "text-ink-subtle" : colour}`}>
      {formatQuantity(value)}
      <span className="ml-1 text-[11px] text-ink-subtle">{formatUnit(unit as never)}</span>
    </span>
  );
}

function LineRow({
  line,
  projectId,
  canWrite,
  onRemove,
}: {
  line: CoverageLine;
  projectId: string;
  canWrite: boolean;
  onRemove: () => void;
}) {
  const reserve = useReserveForProject();
  const release = useReleaseProjectReservations();
  const reservable = Number(line.reservable) > 0;
  const held = Number(line.reserved_for_project) > 0;

  return (
    <Tr>
      <Td className="max-w-0">
        <Link to={`/products/${line.product_id}`} className="block min-w-0 hover:text-brand">
          <span className="block truncate font-medium text-ink">{line.product_name}</span>
          <span className="block truncate text-[11px] text-ink-subtle">
            {[line.product_manufacturer, line.product_part_number].filter(Boolean).join(" · ")}
          </span>
        </Link>
      </Td>
      <Td align="right">
        <span className="tnum whitespace-nowrap font-medium text-ink">
          {formatQuantity(line.required)}
          <span className="ml-1 text-[11px] text-ink-subtle">{formatUnit(line.unit)}</span>
        </span>
      </Td>
      <Td align="right" className="hidden @5xl:table-cell">
        <Quantity value={line.physical} unit={line.unit} />
      </Td>
      <Td align="right" className="hidden @6xl:table-cell">
        <Quantity value={line.reserved_elsewhere} unit={line.unit} tone="warn" />
      </Td>
      <Td align="right" className="hidden @4xl:table-cell">
        <Quantity value={line.available} unit={line.unit} />
      </Td>
      <Td align="right">
        <Quantity value={line.reserved_for_project} unit={line.unit} />
      </Td>
      <Td align="right">
        <Quantity value={line.missing} unit={line.unit} tone="bad" />
      </Td>
      <Td>
        <StatusBadge tone={requirementTone(line.status)}>
          {formatRequirementStatus(line.status)}
        </StatusBadge>
      </Td>
      <Td align="right">
        {canWrite && (
          <div className="flex justify-end gap-1">
            {reservable && (
              <Button
                variant="secondary"
                size="sm"
                disabled={reserve.isPending}
                onClick={() =>
                  void reserve.mutateAsync({ projectId, requirementId: line.requirement_id })
                }
                title={`Réserver ${formatQuantity(line.reservable)}`}
              >
                <Lock className="size-4" aria-hidden />
              </Button>
            )}
            {held ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={release.isPending}
                onClick={() =>
                  void release.mutateAsync({ projectId, requirementId: line.requirement_id })
                }
                aria-label="Libérer cette ligne"
              >
                <Unlock className="size-4" aria-hidden />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={onRemove} aria-label="Retirer la ligne">
                <Trash2 className="size-4" aria-hidden />
              </Button>
            )}
          </div>
        )}
      </Td>
    </Tr>
  );
}

function LineCard({ line }: { line: CoverageLine }) {
  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium text-ink">{line.product_name}</p>
          <p className="truncate text-[11px] text-ink-subtle">
            {[line.product_manufacturer, line.product_part_number].filter(Boolean).join(" · ")}
          </p>
        </div>
        <StatusBadge tone={requirementTone(line.status)} className="shrink-0">
          {formatRequirementStatus(line.status)}
        </StatusBadge>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
        <div>
          <dt className="text-ink-subtle">Besoin</dt>
          <dd className="tnum text-ink">
            {formatQuantity(line.required)} {formatUnit(line.unit)}
          </dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Disponible réel</dt>
          <dd className="tnum text-ink-muted">{formatQuantity(line.available)}</dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Réservé projet</dt>
          <dd className="tnum text-ink-muted">{formatQuantity(line.reserved_for_project)}</dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Manquant</dt>
          <dd className={`tnum ${Number(line.missing) > 0 ? "text-status-out" : "text-ink-muted"}`}>
            {formatQuantity(line.missing)}
          </dd>
        </div>
      </dl>
    </div>
  );
}
